import React, { useId } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import { FormControlWrapper } from "../FormControlWrapper";
import { FormControlOutline } from "../FormControlWrapper/FormControlOutline";
import { FormControlLabel } from "../FormControlWrapper/FormControlLabel";
import { useFormControlInputA11y } from "../FormControlWrapper/FormControlWrapperProvider";
import { splitFormControlWrapperProps } from "../FormControlWrapper/helpers";
import { cn } from "@gettactic/helpers";
import {
  CheckboxInputProps,
  CheckboxInputPropsWithFormControl,
  CheckboxPosition
} from "./CheckboxInput.types";

const checkboxHoverClassBase = "outline outline-[3px] outline-neutral-50";
const checkboxHoverClass = checkboxHoverClassBase
  .split(" ")
  .map((c) => `hover:${c} group-hover:${c}`)
  .join(" ");

const CheckboxInputInput = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxInputProps
>((props, ref) => {
  const {
    checked,
    onCheckedChange,
    disableOutline,
    checkboxPosition,
    className,
    ...rest
  } = props;

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      checked={checked}
      onCheckedChange={(checkedState) => {
        // Convert CheckedState to boolean
        onCheckedChange?.(checkedState === true);
      }}
      className={cn(
        "peer h-4 w-4 shrink-0 rounded-md",
        "border border-neutral-400",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:border-none data-[state=checked]:bg-black data-[state=checked]:text-white",
        checkboxHoverClass,
        className
      )}
      {...rest}
    >
      <CheckboxPrimitive.Indicator
        className={cn("flex items-center justify-center text-current")}
      >
        <CheckIcon className="h-4 w-4" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

CheckboxInputInput.displayName = "CheckboxInputInput";

/**
 * A labelled checkbox for forms, with description, required / error states and three layouts.
 * The label toggles it. Inside react-hook-form use `HookFormCheckboxInput`; for a bare checkbox
 * use `Checkbox` from `primitives/checkbox`.
 */
export const CheckboxInput = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxInputPropsWithFormControl
>((props, ref) => {
  const { checkboxPosition = "bottom", ...restProps } = props;
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(restProps);
  const {
    label,
    description,
    info,
    isRequired,
    isOptional,
    error,
    ...restWrapperProps
  } = wrapperProps;
  const { checked, onCheckedChange, disabled } = inputProps;
  // One id shared by the checkbox and its label: the label then names the checkbox and toggles it
  // natively, with no extra click handling
  const generatedId = useId();
  const checkboxId = restWrapperProps.id ?? generatedId;
  const errorId = `${checkboxId}-error`;

  // The description is not a label (it would lengthen the name); clicking it still toggles
  const handleDescriptionClick = () => {
    if (!disabled && onCheckedChange && checked !== undefined) {
      onCheckedChange(!checked);
    }
  };

  // For left/right positions, we need a custom layout
  if (checkboxPosition === "left" || checkboxPosition === "right") {
    // The box itself carries the invalid state: on its own the message alone
    // reads as body copy and gets lost next to a long acknowledgement.
    const invalidBoxClass =
      error && "border-red-700 data-[state=checked]:bg-red-700";
    const checkbox = (
      <CheckboxInputInput
        ref={ref}
        {...inputProps}
        id={checkboxId}
        aria-invalid={error ? true : undefined}
        aria-required={isRequired || undefined}
        aria-describedby={error ? errorId : undefined}
        className={invalidBoxClass || undefined}
        checkboxPosition={checkboxPosition}
      />
    );

    return (
      <div className="w-full">
        <div className="flex items-start justify-between gap-4">
          {checkboxPosition === "left" && (
            <div className="flex shrink-0 items-center pt-0.5">{checkbox}</div>
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            {label && (
              <FormControlLabel
                htmlFor={checkboxId}
                isRequired={isRequired}
                isOptional={isOptional}
                info={info}
                className={cn(!disabled && "cursor-pointer")}
              >
                {label}
              </FormControlLabel>
            )}
            {description && (
              <p
                className={cn(
                  "text-md mt-1 text-justify text-content",
                  !disabled && onCheckedChange && "cursor-pointer"
                )}
                onClick={handleDescriptionClick}
              >
                {description}
              </p>
            )}
          </div>
          {checkboxPosition === "right" && (
            <div className="flex w-12 shrink-0 items-center justify-end pt-0.5">
              {checkbox}
            </div>
          )}
        </div>
        {/* The left/right layout bypasses FormControlWrapper, so the validation
            message has to be rendered here or it would be swallowed. */}
        {error && (
          <p id={errorId} className="mt-2 font-medium text-red-700 text-sm">
            {error}
          </p>
        )}
        {inputProps.disableOutline !== true && <FormControlOutline />}
      </div>
    );
  }

  // Default bottom position - use FormControlWrapper
  return (
    <FormControlWrapper
      variant={"unstyled"}
      {...restWrapperProps}
      id={checkboxId}
      label={
        label ? (
          <span className={cn("w-full", !disabled && "cursor-pointer")}>
            {label}
          </span>
        ) : undefined
      }
      description={
        description ? (
          <div
            className={cn(!disabled && onCheckedChange && "cursor-pointer")}
            onClick={handleDescriptionClick}
          >
            {description}
          </div>
        ) : undefined
      }
      info={info}
      isRequired={isRequired}
      isOptional={isOptional}
      error={error}
    >
      <CheckboxInputBottom
        ref={ref}
        {...inputProps}
        id={checkboxId}
        checkboxPosition={checkboxPosition}
      />
      {inputProps.disableOutline !== true && <FormControlOutline />}
    </FormControlWrapper>
  );
});

// Inside the wrapper, so it can read the wrapper's ARIA state and message ids
const CheckboxInputBottom = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxInputProps
>((props, ref) => {
  const a11yProps = useFormControlInputA11y();
  return <CheckboxInputInput ref={ref} {...props} {...a11yProps} />;
});
CheckboxInputBottom.displayName = "CheckboxInputBottom";

CheckboxInput.displayName = "CheckboxInput";
